import { Link } from "react-router";

import {
  ChevronRightIcon,
  DetailCardSection,
  DetailList,
  DetailRow,
  formatDateTime,
  IdValue,
} from "@prequel/shared-components";

import ImportJobStatusBadge from "@/components/ImportJobStatusBadge";
import { getJobStatus } from "@/pages/import/jobStatus";
import { getFunnelPercent } from "@/pages/import/StreamsPage/helpers";
import { useLoadContext } from "@/pages/import/StreamsPage/LoadPage";
import { getDuration } from "@/types";

const formatCount = (value: number | null | undefined) => value?.toLocaleString() ?? "-";

type FunnelCard = {
  label: string;
  value: number | undefined;
  percent?: string;
  isError?: boolean;
};

const RowsFunnel = ({ cards, recordsPath }: { cards: FunnelCard[]; recordsPath: string }) => (
  <div className="mb-6 flex items-stretch gap-2">
    {cards.map((card) => (
      <Link
        key={card.label}
        to={card.isError ? `${recordsPath}?has_error=true` : recordsPath}
        className="
          min-w-0 flex-1 rounded-md border border-stone-200 bg-white px-4 py-3
          hover:bg-stone-50
        "
      >
        <div className="text-sm text-stone-600">{card.label}</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span
            className={`
              text-2xl font-semibold
              ${card.isError && card.value ? "text-red-600" : "text-stone-900"}
            `}
          >
            {formatCount(card.value)}
          </span>
          {card.percent && <span className="text-xs text-stone-500">{card.percent}</span>}
        </div>
      </Link>
    ))}
  </div>
);

const LoadDetails = () => {
  const { load, stream } = useLoadContext();
  const processed = load.records_processed;
  const recordsPath = `/import/streams/${load.stream_id}/loads/${load.id}/records`;
  const recordIDs = load.record_ids?.length ? load.record_ids.join("\n") : "None";

  const cards: FunnelCard[] = [
    { label: "Processed Rows", value: processed },
    {
      label: "Transformed",
      value: load.records_transformed,
      percent: getFunnelPercent(load.records_transformed, processed),
    },
    { label: "Valid", value: load.records_valid, percent: getFunnelPercent(load.records_valid, processed) },
    { label: "Loaded", value: load.records_loaded, percent: getFunnelPercent(load.records_loaded, processed) },
    {
      label: "Errored",
      value: load.records_errored,
      percent: getFunnelPercent(load.records_errored, processed),
      isError: true,
    },
  ];

  return (
    <div className="mt-6">
      <section className="mb-6 grid grid-cols-3 gap-4 rounded-md border border-stone-200 bg-white px-4 py-3">
        <div>
          <div className="text-xs tracking-wide text-stone-500 uppercase">Status</div>
          <div className="mt-1">
            <ImportJobStatusBadge status={getJobStatus(load)} />
          </div>
        </div>
        <div>
          <div className="text-xs tracking-wide text-stone-500 uppercase">Total duration</div>
          <div className="mt-1 text-sm text-stone-800">{getDuration(load)}</div>
        </div>
        <div>
          <div className="text-xs tracking-wide text-stone-500 uppercase">Error message</div>
          <div className="mt-1 text-sm text-stone-800">{load.error?.message ?? "—"}</div>
        </div>
      </section>

      <h3 className="mb-2 text-sm font-medium text-stone-800">Summary</h3>
      <RowsFunnel cards={cards} recordsPath={recordsPath} />

      <DetailCardSection title="Details">
        <DetailList>
          <DetailRow label="ID" value={<IdValue id={load.id} />} />
          <DetailRow
            label="Stream"
            value={
              <Link
                to={`/import/streams/${load.stream_id}`}
                className="
                  flex items-center gap-1 text-stone-800
                  hover:underline
                "
              >
                {stream?.name ?? load.stream_id}
                <ChevronRightIcon className="text-stone-400" />
              </Link>
            }
          />
          <DetailRow label="Submitted" value={formatDateTime(load.submitted_at)} />
          <DetailRow label="Started" value={load.started_at ? formatDateTime(load.started_at) : "—"} />
          <DetailRow label="Ended" value={load.ended_at ? formatDateTime(load.ended_at) : "—"} />
          <DetailRow label="Actor" value={`${load.actor_type} (${load.actor_id})`} />
          <DetailRow
            label="Record IDs"
            value={<span className="font-mono text-xs whitespace-pre-line">{recordIDs}</span>}
          />
        </DetailList>
      </DetailCardSection>
    </div>
  );
};

export default LoadDetails;
